import { Layout, Menu } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountStore } from 'store/accountStore';
import { AccountInterface } from 'type/accountType';

import {
  CONTENT_MENU_ITEMS,
  CONTRACT_MENU_ITEMS,
  DEBUG_MENU_ITEMS,
  INGEST_MENU_ITEMS,
  META_DATA_STRUCTURE_MENU_ITEMS,
  REVENUE_MENU_ITEMS,
  SYNC_MENU_ITEMS,
} from 'constants/menu';
import './Sidebar.css';

export interface SidebarProps {
  currentPath: string;
  defaultOpenKeys: string[];
  children: React.ReactNode;
}

const { Content, Footer, Sider } = Layout;

const Sidebar: React.FC<SidebarProps> = observer(({ children, currentPath, defaultOpenKeys }) => {
  const navigate = useNavigate();
  const [imageUrl, setImageUrl] = useState<string>('');
  const [, setUser] = useState<AccountInterface | null>(null);
  const menus = accountStore.getMenus();
  const contentMenuList = useMemo(
    () => CONTENT_MENU_ITEMS!.filter(item => menus.includes(item!.key as string)),
    [menus]
  );

  const contractMenuList = useMemo(
    () => CONTRACT_MENU_ITEMS!.filter(item => menus.includes(item!.key as string)),
    [menus]
  );

  const revenueMenuList = useMemo(
    () => REVENUE_MENU_ITEMS!.filter(item => menus.includes(item!.key as string)),
    [menus]
  );

  const metadataMenuList = useMemo(
    () => META_DATA_STRUCTURE_MENU_ITEMS!.filter(item => menus.includes(item!.key as string)),
    [menus]
  );

  // Ingest 메뉴는 권한 체크 없이 모두에게 표시
  const ingestMenuList = useMemo(
    () => INGEST_MENU_ITEMS!.filter(item => menus.includes(item!.key as string)),
    [menus]
  );

  // Debug 메뉴는 Admin role(is_staff)만 표시
  const debugMenuList = useMemo(
    () => DEBUG_MENU_ITEMS!.filter(item => menus.includes(item!.key as string)),
    [menus]
  );

  const syncMenuList = useMemo(
    () => SYNC_MENU_ITEMS!.filter(item => menus.includes(item!.key as string)),
    [menus]
  );

  useEffect(() => {
    const user = accountStore.getAccount();
    if (user) {
      setImageUrl(user.platform.logo);
      setUser(user);
    }
  }, []);

  return (
    <Layout className="main-layout" hasSider>
      <Sider className="sider" width={200}>
        <button
          className="logo-button"
          onClick={() => navigate('/')}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          <img src={imageUrl} className="logo-image" alt="main_logo" />
        </button>
        <div className="menu-header"></div>
        <div className="content-menu-header">
          <div>Content</div>
        </div>
        <Menu
          className="menu"
          theme="dark"
          mode="inline"
          items={contentMenuList}
          selectedKeys={[currentPath]}
          defaultOpenKeys={defaultOpenKeys}
        />
        <>
          {metadataMenuList.length > 0 && (
            <div>
              <div className="contract-menu-header">
                <div>Metadata</div>
              </div>
              <Menu
                className="menu"
                theme="dark"
                mode="inline"
                items={metadataMenuList}
                selectedKeys={[currentPath]}
                defaultOpenKeys={defaultOpenKeys}
              />
            </div>
          )}
          {ingestMenuList.length > 0 && (
            <div>
              <div className="ingest-menu-header">
                <div>Ingest</div>
              </div>
              <Menu
                className="menu"
                theme="dark"
                mode="inline"
                items={ingestMenuList}
                selectedKeys={[currentPath]}
                defaultOpenKeys={defaultOpenKeys}
              />
            </div>
          )}
          {revenueMenuList.length > 0 && (
            <div>
              <div className="revenue-menu-header">
                <div>Revenue</div>
              </div>
              <Menu
                className="menu"
                theme="dark"
                mode="inline"
                items={revenueMenuList}
                selectedKeys={[currentPath]}
                defaultOpenKeys={defaultOpenKeys}
              />
            </div>
          )}
          {contractMenuList.length > 0 && (
            <div>
              <div className="contract-menu-header">
                <div>Contract</div>
              </div>
              <Menu
                className="menu"
                theme="dark"
                mode="inline"
                items={contractMenuList}
                selectedKeys={[currentPath]}
                defaultOpenKeys={defaultOpenKeys}
              />
            </div>
          )}
          {syncMenuList.length > 0 && (
            <div>
              <div className="contract-menu-header">
                <div>ODX Sync Monitor</div>
              </div>
              <Menu
                className="menu"
                theme="dark"
                mode="inline"
                items={syncMenuList}
                selectedKeys={[currentPath]}
                defaultOpenKeys={defaultOpenKeys}
              />
            </div>
          )}
          {debugMenuList && debugMenuList.length > 0 && (
            <div>
              <div className="contract-menu-header">
                <div>Debug</div>
              </div>
              <Menu
                className="menu"
                theme="dark"
                mode="inline"
                items={debugMenuList}
                selectedKeys={[currentPath]}
                defaultOpenKeys={defaultOpenKeys}
              />
            </div>
          )}
        </>
      </Sider>
      <Layout className="layout-content">
        <Content className="content">{children}</Content>
        <Footer className="footer">ODKMedia, Inc</Footer>
      </Layout>
    </Layout>
  );
});

export default React.memo(Sidebar);
